import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw } = FiIcons;

function CountingGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);

  const animals = ['🦁', '🐘', '🐯', '🦒', '🐵', '🦓', '🦏', '🐆'];

  const generateQuestion = () => {
    const count = Math.floor(Math.random() * (state.age <= 6 ? 5 : 10)) + 1;
    const animal = animals[Math.floor(Math.random() * animals.length)];
    
    // Generate wrong answers
    const wrongAnswers = [];
    while (wrongAnswers.length < 3) {
      const wrong = Math.floor(Math.random() * (state.age <= 6 ? 5 : 10)) + 1;
      if (wrong !== count && !wrongAnswers.includes(wrong)) {
        wrongAnswers.push(wrong);
      }
    }
    
    const allAnswers = [count, ...wrongAnswers].sort(() => Math.random() - 0.5);
    
    return {
      count,
      animal,
      answers: allAnswers,
      correctAnswer: count
    };
  };

  useEffect(() => {
    setCurrentQuestion(generateQuestion());
  }, [state.age]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    setShowResult(true);
    
    const isCorrect = answer === currentQuestion.correctAnswer;
    
    if (isCorrect) {
      dispatch({ type: 'ADD_SCORE', payload: 10 });
    } else {
      dispatch({ type: 'ADD_WRONG_ANSWER' });
    }
    
    setTimeout(() => {
      const newCompleted = questionsCompleted + 1;
      setQuestionsCompleted(newCompleted);
      
      if (newCompleted >= 5) {
        setGameCompleted(true);
        dispatch({ type: 'GAME_COMPLETED' });
      } else {
        setCurrentQuestion(generateQuestion());
        setSelectedAnswer(null);
        setShowResult(false);
      }
    }, 2000);
  };

  const restartGame = () => {
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
    setQuestionsCompleted(0);
    setGameCompleted(false);
  };

  if (!currentQuestion && !gameCompleted) {
    return <div>Loading...</div>;
  }

  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Fantastic Job!
          </h2>
          <p className="font-comic text-jungle-600 mb-6">
            You completed the Counting Safari! You're getting better at counting!
          </p>
          
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartGame}
              className="w-full bg-gradient-to-r from-jungle-500 to-jungle-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Play Again
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Back to Games
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Back</span>
          </button>

          <h1 className="text-3xl font-kid text-jungle-700">Counting Safari 🦁</h1>

          <button
            onClick={restartGame}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Progress */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(questionsCompleted / 5) * 100}%` }}
          className="h-3 bg-jungle-400 rounded-full mb-8 shadow-inner"
          style={{ backgroundColor: '#22c55e' }}
        >
          <div className="w-full h-full bg-gradient-to-r from-jungle-400 to-jungle-500 rounded-full"></div>
        </motion.div>

        {/* Question */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8"
        >
          <h2 className="text-2xl font-kid text-center text-jungle-700 mb-6">
            Count the animals!
          </h2>

          {/* Animals Display */}
          <div className="grid grid-cols-5 gap-4 justify-items-center mb-8">
            {Array.from({ length: currentQuestion.count }, (_, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-4xl"
              >
                {currentQuestion.animal}
              </motion.div>
            ))}
          </div>

          {/* Answer Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentQuestion.answers.map((answer) => (
              <motion.button
                key={answer}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => !showResult && handleAnswerSelect(answer)}
                disabled={showResult}
                className={`p-6 rounded-2xl font-kid text-2xl shadow-lg transition-all duration-200 ${
                  showResult
                    ? answer === currentQuestion.correctAnswer
                      ? 'bg-green-400 text-white'
                      : answer === selectedAnswer
                      ? 'bg-red-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                    : 'bg-gradient-to-br from-safari-200 to-safari-300 text-jungle-700 hover:from-safari-300 hover:to-safari-400'
                }`}
              >
                {answer}
              </motion.button>
            ))}
          </div>

          {/* Result Message */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 text-center"
              >
                {selectedAnswer === currentQuestion.correctAnswer ? (
                  <div className="text-green-600 font-kid text-xl">
                    🎉 Excellent! You counted correctly!
                  </div>
                ) : (
                  <div className="text-red-600 font-kid text-xl">
                    Try again! The correct answer is {currentQuestion.correctAnswer}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default CountingGame;